import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authApi, checkAuth } from '../../api/auth';
import { LoginCredentials, User, LoginResponse } from '../../types/auth';
import { AxiosError } from 'axios';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isLoggingOut: boolean;
}

// localStorageからトークンを読み込んで初期認証状態を設定
const getInitialAuthState = (): AuthState => {
  const accessToken = localStorage.getItem('accessToken');
  const user = localStorage.getItem('user');
  
  // 初期状態では認証されていないとして扱う（verifyAuthで検証）
  return {
    user: user ? JSON.parse(user) : null,
    isAuthenticated: false, // デフォルトはfalse（トークンがあってもverifyAuthで検証する）
    isLoading: false,
    error: null,
    isLoggingOut: false,
  };
};

const initialState: AuthState = getInitialAuthState();

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials);
      if (response.access_token) {
        localStorage.setItem('accessToken', response.access_token);
      }
      if (response.id_token) {
        localStorage.setItem('idToken', response.id_token);
      }
      if (response.refresh_token) {
        localStorage.setItem('refreshToken', response.refresh_token);
      }
      return response;
    } catch (err) {
      if (err instanceof AxiosError && err.response) {
        return rejectWithValue(err.response.data.message || 'ログインに失敗しました');
      }
      return rejectWithValue('ログインに失敗しました');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    // ログアウト開始時に即座にクライアント側をクリア
    const clearClientState = () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      console.log('🔒 ログアウト: ローカルストレージをクリアしました');
    };

    // 最初にクライアント側をクリア（他の処理よりも優先）
    clearClientState();

    try {
      // APIコール（失敗してもログアウト処理は続行）
      await authApi.logout();
      console.log('✅ ログアウトAPI成功');
    } catch (error) {
      console.warn('⚠️ ログアウトAPIエラー（クライアント側のクリーンアップは完了済み）:', error);
    }

    return null; // 常に成功として扱う
  }
);

export const verifyAuth = createAsyncThunk(
  'auth/verify',
  async (_, { rejectWithValue, getState }) => {
    // ログアウト中は認証チェックをスキップ
    const state = getState() as { auth: AuthState };
    if (state.auth.isLoggingOut) {
      console.log('🔒 ログアウト中のため認証チェックをスキップ');
      return rejectWithValue('ログアウト中');
    }

    // トークンがない場合もスキップ
    const hasToken = localStorage.getItem('accessToken');
    if (!hasToken) {
      console.log('🔒 トークンがないため認証チェックをスキップ');
      return rejectWithValue('トークンなし');
    }

    try {
      const user = await checkAuth();
      return user;
    } catch (err) {
      if (err instanceof AxiosError && err.response) {
        return rejectWithValue(err.response.data.message || '認証の確認に失敗しました');
      }
      return rejectWithValue('認証の確認に失敗しました');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLoggingOut: (state, action) => {
      state.isLoggingOut = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ログイン
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        console.log('🔑 Redux login.fulfilled - トークン保存開始', action.payload);
        state.isLoading = false;
        state.isAuthenticated = true;
        // ロールを小文字に統一してUser型に合わせる
        state.user = {
          ...action.payload.user,
          role: action.payload.user.role.toLowerCase() as 'admin' | 'user'
        };
        state.error = null;
        state.isLoggingOut = false;
        
        // TokenをlocalStorageに保存
        if (action.payload.access_token) {
          localStorage.setItem('accessToken', action.payload.access_token);
          console.log('✅ AccessToken保存完了');
        }
        if (action.payload.id_token) {
          localStorage.setItem('idToken', action.payload.id_token);
          console.log('✅ IdToken保存完了');
        }
        if (action.payload.refresh_token) {
          localStorage.setItem('refreshToken', action.payload.refresh_token);
          console.log('✅ RefreshToken保存完了');
        }
        if (action.payload.user) {
          localStorage.setItem('user', JSON.stringify(action.payload.user));
          console.log('✅ User情報保存完了');
        }
        console.log('🎉 Redux login.fulfilled - すべて完了');
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload as string || 'ログインに失敗しました';
      })
      // ログアウト
      .addCase(logout.pending, (state) => {
        state.isLoggingOut = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.error = null;
        state.isLoggingOut = false;
      })
      .addCase(logout.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoggingOut = false;
      })
      // 認証確認
      .addCase(verifyAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyAuth.fulfilled, (state, action) => {
        if (!state.isLoggingOut) {
          state.isLoading = false;
          state.isAuthenticated = true;
          // ロールを小文字に統一してUser型に合わせる
          state.user = {
            ...action.payload,
            role: action.payload.role.toLowerCase() as 'admin' | 'user'
          };
          state.error = null;
        }
      })
      .addCase(verifyAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload as string || '認証の確認に失敗しました';
        // 認証失敗時はlocalStorageをクリア
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        console.log('🔒 認証失敗: トークンをクリアしました');
      });
  },
});

export const { clearError, setLoggingOut } = authSlice.actions;
export default authSlice.reducer; 